import { useState, useRef } from 'react'
import { Link } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listClientNotes,
  createClientNote,
  updateClientNote,
  deleteClientNote,
  listClientLinks,
  createClientLink,
  deleteClientLink,
  listClientAttachments,
  uploadClientAttachment,
  downloadClientAttachmentUrl,
  deleteClientAttachment,
  CLIENT_NOTE_CATEGORIES,
  type ClientNote,
  type ClientNoteCategory,
} from '../../lib/api'
import {
  Plus,
  Trash2,
  Edit3,
  ExternalLink,
  FileText,
  Image,
  File,
  StickyNote,
  Paperclip,
  Link2,
} from 'lucide-react'
import { DownloadButton } from '../DownloadButton'

export const CATEGORY_LABELS: Record<string, string> = {
  brand_voice: 'Brand voice',
  goals: 'Goals',
  audience: 'Audience',
  process: 'Process',
  lifecycle: 'Lifecycle',
  general: 'General',
}

/* ═══════════════════════════════════════════════════════════════════
 * NOTES — categorized ongoing client details (brand voice, goals, …)
 * ═══════════════════════════════════════════════════════════════════ */

export function ClientNotesPanel({ clientId, canManage }: { clientId: string; canManage: boolean }) {
  const qc = useQueryClient()
  const [filter, setFilter] = useState<string>('all')
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['client-notes', clientId],
    queryFn: () => listClientNotes(clientId),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['client-notes', clientId] })
    qc.invalidateQueries({ queryKey: ['events', { entityType: 'client', entityId: clientId }] })
  }

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => deleteClientNote(clientId, noteId),
    onSuccess: invalidate,
  })

  const notes = data?.items ?? []
  const filtered = filter === 'all' ? notes : notes.filter((n) => n.category === filter)
  const usedCategories = [...new Set(notes.map((n) => n.category))]

  return (
    <div className="task-detail-section">
      <div className="task-detail-section__header">
        <h3>Notes</h3>
        {canManage && !adding && (
          <button className="btn btn-ghost btn-xs" onClick={() => setAdding(true)}>
            <Plus size={12} /> Add note
          </button>
        )}
      </div>

      {/* Category filter */}
      {notes.length > 0 && (
        <div className="client-notes__filters">
          <button
            className={`chip chip-sm client-notes__filter${filter === 'all' ? ' client-notes__filter--active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          {usedCategories.map((cat) => (
            <button
              key={cat}
              className={`chip chip-sm client-notes__filter${filter === cat ? ' client-notes__filter--active' : ''}`}
              onClick={() => setFilter(cat)}
            >
              {CATEGORY_LABELS[cat] ?? cat}
            </button>
          ))}
        </div>
      )}

      {adding && (
        <NoteForm
          clientId={clientId}
          onDone={() => {
            setAdding(false)
            invalidate()
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {isLoading ? (
        <p className="text-muted text-xs">Loading…</p>
      ) : filtered.length === 0 && !adding ? (
        <p className="task-detail-empty">
          No notes yet.{canManage ? ' Capture brand voice, goals, audience, or process details here.' : ''}
        </p>
      ) : (
        <div className="client-notes__list">
          {filtered.map((note) =>
            editingId === note.id ? (
              <NoteForm
                key={note.id}
                clientId={clientId}
                note={note}
                onDone={() => {
                  setEditingId(null)
                  invalidate()
                }}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <div key={note.id} className="client-note-card">
                <div className="client-note-card__header">
                  <span className="chip chip-sm">{CATEGORY_LABELS[note.category] ?? note.category}</span>
                  <span className="client-note-card__title">{note.title}</span>
                  {canManage && (
                    <div className="client-note-card__actions">
                      <button
                        className="btn-icon"
                        onClick={() => setEditingId(note.id)}
                        aria-label="Edit note"
                      >
                        <Edit3 size={12} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => {
                          if (confirm(`Delete note "${note.title}"? This can't be undone.`)) {
                            deleteMutation.mutate(note.id)
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        aria-label="Delete note"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                </div>
                <p className="client-note-card__body">{note.body}</p>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  )
}

function NoteForm({
  clientId,
  note,
  onDone,
  onCancel,
}: {
  clientId: string
  note?: ClientNote
  onDone: () => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(note?.title ?? '')
  const [category, setCategory] = useState<ClientNoteCategory>(
    (note?.category as ClientNoteCategory) ?? 'general',
  )
  const [body, setBody] = useState(note?.body ?? '')

  // Dirty = differs from the saved note (or any content on a new note)
  const isDirty = note
    ? title !== note.title || category !== note.category || body !== note.body
    : title.trim() !== '' || body.trim() !== ''

  const handleCancel = () => {
    if (isDirty && !confirm('Discard unsaved changes to this note?')) return
    onCancel()
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      note
        ? updateClientNote(clientId, note.id, { title, category, body })
        : createClientNote(clientId, { title, category, body }),
    onSuccess: onDone,
  })

  return (
    <div className="client-note-form">
      <div className="client-note-form__row">
        <input
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="task-detail-input"
          autoFocus
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as ClientNoteCategory)}
          className="task-detail-input client-note-form__category"
        >
          {CLIENT_NOTE_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {CATEGORY_LABELS[cat]}
            </option>
          ))}
        </select>
      </div>
      <textarea
        placeholder="Write the note…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={5}
        className="task-detail-textarea"
      />
      <div className="task-detail-section__actions">
        <button
          className="btn btn-primary btn-xs"
          onClick={() => saveMutation.mutate()}
          disabled={!title.trim() || !body.trim() || saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn-ghost btn-xs" onClick={handleCancel}>
          Cancel
        </button>
        {(!title.trim() || !body.trim()) && (
          <span className="text-muted text-xs">Title and body are required</span>
        )}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * FILES + LINKS — reference material at the client level
 * ═══════════════════════════════════════════════════════════════════ */

export function ClientFilesPanel({ clientId, canManage }: { clientId: string; canManage: boolean }) {
  const qc = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [addingLink, setAddingLink] = useState(false)
  const [url, setUrl] = useState('')
  const [label, setLabel] = useState('')

  const { data: attachmentsData, isLoading: loadingFiles } = useQuery({
    queryKey: ['client-attachments', clientId],
    queryFn: () => listClientAttachments(clientId),
  })

  const { data: linksData, isLoading: loadingLinks } = useQuery({
    queryKey: ['client-links', clientId],
    queryFn: () => listClientLinks(clientId),
  })

  const invalidateEvents = () =>
    qc.invalidateQueries({ queryKey: ['events', { entityType: 'client', entityId: clientId }] })

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      setUploading(true)
      return uploadClientAttachment(clientId, file)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-attachments', clientId] })
      invalidateEvents()
      setUploading(false)
    },
    onError: (err: Error) => {
      setUploading(false)
      alert(`Upload failed: ${err.message}`)
    },
  })

  const deleteFileMutation = useMutation({
    mutationFn: (attachmentId: string) => deleteClientAttachment(clientId, attachmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-attachments', clientId] })
      invalidateEvents()
    },
  })

  const addLinkMutation = useMutation({
    mutationFn: () => createClientLink(clientId, { url, label: label || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-links', clientId] })
      invalidateEvents()
      setUrl('')
      setLabel('')
      setAddingLink(false)
    },
  })

  const deleteLinkMutation = useMutation({
    mutationFn: (linkId: string) => deleteClientLink(clientId, linkId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-links', clientId] })
      invalidateEvents()
    },
  })

  const attachments = attachmentsData?.items ?? []
  const links = linksData?.items ?? []

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const duplicate = attachments.find((a) => a.filename === file.name)
      if (
        !duplicate ||
        confirm(`"${file.name}" is already attached. Upload another copy anyway?`)
      ) {
        uploadMutation.mutate(file)
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <>
      {/* Files */}
      <div className="task-detail-section">
        <div className="task-detail-section__header">
          <h3>Files</h3>
          {canManage && (
            <button
              className="btn btn-ghost btn-xs"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Plus size={12} /> {uploading ? 'Uploading…' : 'Upload'}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="sr-only"
            onChange={handleFileChange}
            aria-label="Upload file"
          />
        </div>

        {loadingFiles ? (
          <p className="text-muted text-xs">Loading…</p>
        ) : attachments.length === 0 ? (
          <p className="task-detail-empty">
            No files yet.{canManage ? ' Upload brand assets, guidelines, or reference material.' : ''}
          </p>
        ) : (
          <ul className="task-detail-file-list">
            {attachments.map((att) => (
              <li key={att.id} className="task-detail-file-item">
                <FileIcon mimeType={att.mimeType} />
                <div className="task-detail-file-info">
                  <span className="task-detail-file-name">{att.filename}</span>
                  <span className="task-detail-file-meta text-muted text-xs">
                    {formatFileSize(att.sizeBytes)}
                    {' · '}
                    {new Date(att.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
                <div className="task-detail-file-actions">
                  <DownloadButton
                    url={downloadClientAttachmentUrl(clientId, att.id)}
                    filename={att.filename}
                  />
                  {canManage && (
                    <button
                      className="btn-icon"
                      onClick={() => {
                        if (confirm(`Delete "${att.filename}"? The file will be removed from storage.`)) {
                          deleteFileMutation.mutate(att.id)
                        }
                      }}
                      disabled={deleteFileMutation.isPending}
                      aria-label="Delete file"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Links */}
      <div className="task-detail-section">
        <div className="task-detail-section__header">
          <h3>Links</h3>
          {canManage && !addingLink && (
            <button className="btn btn-ghost btn-xs" onClick={() => setAddingLink(true)}>
              <Plus size={12} /> Add link
            </button>
          )}
        </div>

        {addingLink && (
          <div className="task-detail-link-form">
            <input
              type="url"
              placeholder="https://…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="task-detail-input"
              autoFocus
            />
            <input
              type="text"
              placeholder="Label (optional)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="task-detail-input"
            />
            <div className="task-detail-section__actions">
              <button
                className="btn btn-primary btn-xs"
                onClick={() => addLinkMutation.mutate()}
                disabled={!url || addLinkMutation.isPending}
              >
                Add
              </button>
              <button className="btn btn-ghost btn-xs" onClick={() => setAddingLink(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {loadingLinks ? (
          <p className="text-muted text-xs">Loading…</p>
        ) : links.length === 0 && !addingLink ? (
          <p className="task-detail-empty">No links yet.</p>
        ) : (
          <ul className="task-detail-link-list">
            {links.map((link) => (
              <li key={link.id} className="task-detail-link-item">
                <ExternalLink size={14} className="text-muted" />
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="task-detail-link-url"
                  title={link.url}
                >
                  {link.label || link.url}
                </a>
                {canManage && (
                  <button
                    className="task-detail-link-delete"
                    onClick={() => {
                      if (confirm(`Remove link "${link.label || link.url}"?`)) {
                        deleteLinkMutation.mutate(link.id)
                      }
                    }}
                    disabled={deleteLinkMutation.isPending}
                    aria-label="Remove link"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * READ-ONLY CONTEXT — the client's knowledge surfaced inside a task
 * ═══════════════════════════════════════════════════════════════════ */

export function ClientContextPanel({ clientId }: { clientId: string }) {
  const { data: notesData, isLoading: loadingNotes } = useQuery({
    queryKey: ['client-notes', clientId],
    queryFn: () => listClientNotes(clientId),
  })

  const { data: attachmentsData } = useQuery({
    queryKey: ['client-attachments', clientId],
    queryFn: () => listClientAttachments(clientId),
  })

  const { data: linksData } = useQuery({
    queryKey: ['client-links', clientId],
    queryFn: () => listClientLinks(clientId),
  })

  const notes = notesData?.items ?? []
  const attachments = attachmentsData?.items ?? []
  const links = linksData?.items ?? []

  // Group notes by category for scannable context
  const grouped = new Map<string, typeof notes>()
  for (const note of notes) {
    const list = grouped.get(note.category) ?? []
    list.push(note)
    grouped.set(note.category, list)
  }

  return (
    <div className="task-detail-section">
      <div className="task-detail-section__header">
        <h3>Client context</h3>
        <Link to={`/clients/${clientId}`} className="btn btn-ghost btn-xs">
          <ExternalLink size={12} /> Open client page
        </Link>
      </div>
      <p className="text-muted text-xs client-context__hint">
        Read-only. Edit these on the client page.
      </p>

      {loadingNotes ? (
        <p className="text-muted text-xs">Loading…</p>
      ) : notes.length === 0 && attachments.length === 0 && links.length === 0 ? (
        <p className="task-detail-empty">No client notes, files, or links yet.</p>
      ) : (
        <>
          {[...grouped.entries()].map(([category, catNotes]) => (
            <div key={category} className="client-context__group">
              <h4 className="client-context__group-title">
                <StickyNote size={12} /> {CATEGORY_LABELS[category] ?? category}
              </h4>
              {catNotes.map((note) => (
                <div key={note.id} className="client-note-card client-note-card--readonly">
                  <span className="client-note-card__title">{note.title}</span>
                  <p className="client-note-card__body">{note.body}</p>
                </div>
              ))}
            </div>
          ))}

          {attachments.length > 0 && (
            <div className="client-context__group">
              <h4 className="client-context__group-title">
                <Paperclip size={12} /> Files
              </h4>
              <ul className="task-detail-file-list">
                {attachments.map((att) => (
                  <li key={att.id} className="task-detail-file-item">
                    <FileIcon mimeType={att.mimeType} />
                    <div className="task-detail-file-info">
                      <span className="task-detail-file-name">{att.filename}</span>
                      <span className="task-detail-file-meta text-muted text-xs">
                        {formatFileSize(att.sizeBytes)}
                      </span>
                    </div>
                    <DownloadButton
                      url={downloadClientAttachmentUrl(clientId, att.id)}
                      filename={att.filename}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {links.length > 0 && (
            <div className="client-context__group">
              <h4 className="client-context__group-title">
                <Link2 size={12} /> Links
              </h4>
              <ul className="task-detail-link-list">
                {links.map((link) => (
                  <li key={link.id} className="task-detail-link-item">
                    <ExternalLink size={14} className="text-muted" />
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="task-detail-link-url"
                      title={link.url}
                    >
                      {link.label || link.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * HELPERS
 * ═══════════════════════════════════════════════════════════════════ */

function FileIcon({ mimeType }: { mimeType: string | null }) {
  if (mimeType?.startsWith('image/')) return <Image size={16} className="text-accent" />
  if (mimeType?.includes('pdf')) return <FileText size={16} className="text-danger" />
  return <File size={16} className="text-muted" />
}

function formatFileSize(bytes: number | null): string {
  if (bytes == null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
